import { useEffect, useState } from "react";
import { isIOS, isMobile } from "react-device-detect";

export const useIsKeyboardOpen = (shouldActivate = false): boolean => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isMobile) return;
    if (!shouldActivate) return;

    let activeElement: HTMLElement | null = null;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;

      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        activeElement = target; // Track the currently focused element
        setIsKeyboardOpen(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;

      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        // Check if focus has moved to another input or textarea
        if (document.activeElement !== activeElement) {
          setIsKeyboardOpen(false);
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [shouldActivate]);

  return isKeyboardOpen;
};

// Alternative implementation using visualViewport
// Keeping this for reference, if we want to try to use it in the future
export const useIsKeyboard = () => {
  const threshold = 200; // Threshold to determine if the keyboard is open
  const [isKeyboardOpen, setIsOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [visualHeight, setVisualHeight] = useState(0);

  let deviceKeyboardExtraHeight = 0;

  if (isIOS) {
    deviceKeyboardExtraHeight = 34;
  }
  const bottomBar = 110; // the height of the bottom bar
  const bottomSectionHeight = bottomBar + deviceKeyboardExtraHeight;

  useEffect(() => {
    if (!isMobile) return;
    if (typeof window === "undefined" || !window.visualViewport) return;

    const handleResize = () => {
      // window.innerHeight total height of the viewport
      const layoutHeight = window.innerHeight;

      // window.visualViewport.height height of the viewport seen (excluding the keyboard)
      const visualViewPortHeight =
        window.visualViewport!.height + bottomSectionHeight;

      const delta = layoutHeight - visualViewPortHeight;

      if (delta > threshold) {
        setIsOpen(true);
        setKeyboardHeight(delta);
        setVisualHeight(visualViewPortHeight);
      } else {
        setIsOpen(false);
        setKeyboardHeight(0);
        setVisualHeight(0);
      }
    };

    window.visualViewport.addEventListener("resize", handleResize);
    window.visualViewport.addEventListener("scroll", handleResize); // Safari iOS quirk

    // Initial run (in case it's already open)
    handleResize();

    return () => {
      window.visualViewport!.removeEventListener("resize", handleResize);
      window.visualViewport!.removeEventListener("scroll", handleResize);
    };
  }, [threshold, bottomSectionHeight]);

  return { isKeyboardOpen, keyboardHeight, visualHeight, bottomSectionHeight };
};
